<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import type { Swiper as SwiperClass } from 'swiper';
import { Navigation } from 'swiper/modules';
import type { NavigationOptions } from 'swiper/types';
import 'swiper/css';
import 'swiper/css/navigation';
import { EventCard } from '~/entities/event-card';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import type { IEventPublisherDataDTO } from '~/shared/api/client/events/events-publisher.dto';
import { mapPublisherEvents } from '~/shared/api/client/events/events-publisher.dto';
import {
  CLIENT_EVENTS_MAIN_URL,
  CLIENT_EVENTS_MAIN_KEY,
} from '~/shared/api/client/publisher/publisher.const';
import { useSwiperControlsVisibility } from '~/shared/lib/useSwiperControlsVisibility';

const props = withDefaults(
  defineProps<{
    excludeEventId?: number;
    title?: string;
    showPast?: boolean;
  }>(),
  {
    excludeEventId: undefined,
    title: 'Другие мероприятия',
    showPast: false,
  },
);

const { $viewport } = useNuxtApp();

const { data: publisherData } = useCustomFetch<IEventPublisherDataDTO[]>(
  CLIENT_EVENTS_MAIN_URL,
  { key: CLIENT_EVENTS_MAIN_KEY },
);

const events = computed(() => {
  if (!publisherData.value) return [];

  const now = new Date();

  return mapPublisherEvents(publisherData.value).filter(
    (item) =>
      (props.excludeEventId == null ||
        item.event.id !== props.excludeEventId) &&
      (props.showPast || new Date(item.event.end_time) >= now),
  );
});

const itemsCount = computed(() => events.value.length);
const isControlVisible = useSwiperControlsVisibility(
  itemsCount,
  $viewport.breakpoint,
);

const swiperRef = ref<{ swiper: SwiperClass } | null>(null);
const navigationOptions = ref<NavigationOptions>({
  prevEl: null,
  nextEl: null,
});
const prevEl = ref<HTMLElement | null>(null);
const nextEl = ref<HTMLElement | null>(null);

onMounted(() => {
  if (prevEl.value && nextEl.value) {
    navigationOptions.value = {
      prevEl: prevEl.value,
      nextEl: nextEl.value,
    };
  }

  watch(
    () => isControlVisible.value,
    (newValue) => {
      const swiper = swiperRef.value?.swiper;
      if (!swiper) return;

      if (!newValue) {
        swiper.navigation?.destroy();
      } else {
        nextTick(() => {
          if (prevEl.value && nextEl.value && swiper.navigation) {
            swiper.navigation.destroy();
            swiper.navigation.init();
            swiper.navigation.update();
          }
        });
      }
    },
  );
});
</script>

<template>
  <div v-if="events.length" :class="$style.section">
    <div :class="$style.header">
      <h3 :class="$style.title">{{ title }}</h3>
      <div v-show="isControlVisible" :class="$style.controls">
        <div ref="prevEl" :class="$style.controlBtn">
          <Icon :class="$style.controlIcon" name="my-icon:i-arrow-left" />
        </div>
        <div ref="nextEl" :class="$style.controlBtn">
          <Icon :class="$style.controlIcon" name="my-icon:i-arrow-right" />
        </div>
      </div>
    </div>

    <Swiper
      ref="swiperRef"
      :observer="true"
      :observe-parents="true"
      :class="$style.swiperContainer"
      :modules="[Navigation]"
      :grab-cursor="true"
      :loop="events.length > 3"
      :navigation="navigationOptions"
      :slides-per-view="1.15"
      :slides-per-group="1"
      :breakpoints="{
        480: { slidesPerView: 2 },
        768: { slidesPerView: 3 },
      }"
    >
      <SwiperSlide
        v-for="item in events"
        :key="item.event.id"
        :class="[$style.slide, events.length <= 3 && $style.slideClosing]"
      >
        <EventCard
          :event="item.event"
          :image-url="item.imageUrl"
          :place="item.place"
        />
      </SwiperSlide>
    </Swiper>

    <div :class="$style.footer">
      <Button theme="outline" size="xl" to="/events" :class="$style.footerBtn">
        Смотреть все
      </Button>
    </div>
  </div>
</template>

<style module lang="scss">
.section {
  display: flex;
  flex-direction: column;
  gap: var(--space-3x);
  padding: 0 0 var(--space-2x);
  overflow: hidden;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--space-2-5x);
}

.title {
  @include text_h3;
  color: var(--color-black-1);
  margin: 0;
}

.controls {
  display: flex;

  path {
    fill: var(--color-black-1);
  }
}

.controlBtn {
  display: flex;
  width: 100px;
  height: 64px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: 1px solid var(--color-gray-line);

  & + & {
    border-left: none;
  }
}

.controlIcon {
  width: 20px;
  height: 20px;
}

.swiperContainer {
  width: 100%;
  border-top: 1px solid var(--color-gray-line);
  border-bottom: 1px solid var(--color-gray-line);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background: var(--color-gray-line);
    z-index: 1;
    pointer-events: none;
  }
}

.slide {
  height: auto;
  box-sizing: border-box;
  border-left: 1px solid var(--color-gray-line);
}

// closes the last card's right edge when slides don't fill the container;
// applied only without loop — loop mode reorders slides in the DOM
.slideClosing:last-child {
  border-right: 1px solid var(--color-gray-line);
}

.footer {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  padding: var(--space-2-5x);
  background: var(--color-gray-bg);
  border: 1px solid var(--color-gray-line);
}

.footerBtn {
  width: 100%;
  height: 60px;
  background: var(--color-white);
}

@media (min-width: 768px) {
  .section {
    padding-left: 20px;
    padding-right: 20px;
  }

  .header {
    margin-left: -20px;
    margin-right: -20px;
  }
}

@media (max-width: 1379px) {
  .title {
    font-size: 32px;
  }
}

@media (max-width: 1023px) {
  .title {
    font-size: 24px;
  }
}

@media (max-width: 479px) {
  .section {
    gap: var(--space-2x);
  }

  .header {
    padding: 0 var(--space-2x);
  }

  .title {
    font-size: 16px;
  }

  .slide {
    min-width: 240px;
    min-height: 288px;
  }

  .controlBtn {
    width: 72px;
    height: 46px;
  }

  .footer {
    height: 80px;
    padding: var(--space-2x);
  }

  .footerBtn {
    height: 48px;
  }
}
</style>
